/**
 * Seções de campos simples do formulário do protocolo (E74.F1): identificação,
 * signatário, geolocalização, dispositivo, biometria e observações. Cada seção é
 * controlada pelo `ProtocoloForm` via `patch` — nada aqui tem estado próprio.
 */
import type { ReactNode } from 'react';

import { Field, Input, Select, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { FONTES_COORDENADA, TIPOS_ASSINATURA } from '@/lib/exame_eletronico';
import { formatCpf } from '@/lib/masks';

import type { ProtocoloFormErros, ProtocoloFormState } from './protocolo_form_state';

const t = ptBR.exameEletronico.form;

export type Patch = (parcial: Partial<ProtocoloFormState>) => void;

export interface SecaoProps {
  idPrefix: string;
  state: ProtocoloFormState;
  patch: Patch;
  disabled: boolean;
  erros: ProtocoloFormErros;
}

export function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <fieldset className="min-w-0">
      <legend className="mb-sc-2 font-display text-[13px] uppercase tracking-[0.06em] text-fg-muted">
        {titulo}
      </legend>
      <div className="grid gap-sc-3 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

interface CampoTextoProps {
  id: string;
  label: string;
  value: string;
  onChange: (valor: string) => void;
  disabled: boolean;
  hint?: string;
  error?: string;
  mono?: boolean;
  type?: string;
  inputMode?: 'numeric' | 'decimal' | 'email' | 'tel';
  maxLength?: number;
  colSpan?: 'full';
}

export function CampoTexto(props: CampoTextoProps) {
  const { id, label, value, onChange, disabled, hint, error, mono, type, colSpan } = props;
  return (
    <Field
      htmlFor={id}
      label={label}
      {...(hint !== undefined ? { description: hint } : {})}
      {...(error !== undefined ? { error } : {})}
      {...(colSpan === 'full' ? { className: 'sm:col-span-2' } : {})}
    >
      <Input
        id={id}
        data-testid={id}
        value={value}
        disabled={disabled}
        type={type ?? 'text'}
        onChange={(e) => onChange(e.target.value)}
        {...(mono ? { className: 'font-mono' } : {})}
        {...(props.inputMode !== undefined ? { inputMode: props.inputMode } : {})}
        {...(props.maxLength !== undefined ? { maxLength: props.maxLength } : {})}
      />
    </Field>
  );
}

export function SecaoIdentificacao({
  idPrefix,
  state,
  patch,
  disabled,
  erros,
  ocultarCodigoDeclarado = false,
}: SecaoProps & { ocultarCodigoDeclarado?: boolean }) {
  return (
    <Secao titulo={t.secaoIdentificacao}>
      <CampoTexto
        id={`${idPrefix}-plataforma`}
        label={t.plataforma}
        value={state.plataforma}
        onChange={(v) => patch({ plataforma: v })}
        disabled={disabled}
        maxLength={120}
      />
      <CampoTexto
        id={`${idPrefix}-codigo`}
        label={t.codigoVerificacao}
        value={state.codigoVerificacao}
        onChange={(v) => patch({ codigoVerificacao: v })}
        disabled={disabled}
        mono
        maxLength={255}
      />
      <CampoTexto
        id={`${idPrefix}-envelope`}
        label={t.envelopeId}
        value={state.envelopeId}
        onChange={(v) => patch({ envelopeId: v })}
        disabled={disabled}
        mono
        maxLength={255}
      />
      <CampoTexto
        id={`${idPrefix}-assinatura-id`}
        label={t.assinaturaId}
        value={state.assinaturaId}
        onChange={(v) => patch({ assinaturaId: v })}
        disabled={disabled}
        mono
        maxLength={255}
      />
      {/* E78.F2: no protocolo do CASO o código declarado não entra — ele é da
          peça, e cada uma tem o seu. */}
      {ocultarCodigoDeclarado ? null : (
        <CampoTexto
          id={`${idPrefix}-hash`}
          label={t.hashDeclarado}
          hint={t.hashDeclaradoHint}
          {...(erros.hashDeclarado !== undefined ? { error: erros.hashDeclarado } : {})}
          value={state.hashDeclarado}
          onChange={(v) => patch({ hashDeclarado: v })}
          disabled={disabled}
          mono
          maxLength={128}
          colSpan="full"
        />
      )}
      <Field htmlFor={`${idPrefix}-tipo`} label={t.tipoAssinatura}>
        <Select
          id={`${idPrefix}-tipo`}
          data-testid={`${idPrefix}-tipo`}
          value={state.tipoAssinatura}
          disabled={disabled}
          onChange={(e) =>
            patch({ tipoAssinatura: e.target.value as ProtocoloFormState['tipoAssinatura'] })
          }
        >
          {TIPOS_ASSINATURA.map((tipo) => (
            <option key={tipo} value={tipo}>
              {t.tipoAssinaturaOpcoes[tipo]}
            </option>
          ))}
        </Select>
      </Field>
    </Secao>
  );
}

export function SecaoSignatario({ idPrefix, state, patch, disabled }: SecaoProps) {
  return (
    <Secao titulo={t.secaoSignatario}>
      <CampoTexto
        id={`${idPrefix}-sig-nome`}
        label={t.signatarioNome}
        value={state.signatarioNome}
        onChange={(v) => patch({ signatarioNome: v })}
        disabled={disabled}
        maxLength={255}
      />
      <CampoTexto
        id={`${idPrefix}-sig-cpf`}
        label={t.signatarioCpf}
        value={state.signatarioCpf}
        onChange={(v) => patch({ signatarioCpf: formatCpf(v) })}
        disabled={disabled}
        mono
        inputMode="numeric"
        maxLength={14}
      />
      <CampoTexto
        id={`${idPrefix}-sig-email`}
        label={t.signatarioEmail}
        value={state.signatarioEmail}
        onChange={(v) => patch({ signatarioEmail: v })}
        disabled={disabled}
        type="email"
        inputMode="email"
        maxLength={255}
      />
      <CampoTexto
        id={`${idPrefix}-sig-telefone`}
        label={t.signatarioTelefone}
        value={state.signatarioTelefone}
        onChange={(v) => patch({ signatarioTelefone: v })}
        disabled={disabled}
        inputMode="tel"
        maxLength={32}
      />
    </Secao>
  );
}

export function SecaoCoordenada({ idPrefix, state, patch, disabled, erros }: SecaoProps) {
  return (
    <Secao titulo={t.secaoCoordenada}>
      <CampoTexto
        id={`${idPrefix}-latitude`}
        label={t.latitude}
        {...(erros.coordenada !== undefined ? { error: erros.coordenada } : {})}
        value={state.latitude}
        onChange={(v) => patch({ latitude: v })}
        disabled={disabled}
        mono
        inputMode="decimal"
      />
      <CampoTexto
        id={`${idPrefix}-longitude`}
        label={t.longitude}
        value={state.longitude}
        onChange={(v) => patch({ longitude: v })}
        disabled={disabled}
        mono
        inputMode="decimal"
      />
      <CampoTexto
        id={`${idPrefix}-precisao`}
        label={t.precisaoM}
        value={state.precisaoM}
        onChange={(v) => patch({ precisaoM: v })}
        disabled={disabled}
        mono
        inputMode="decimal"
      />
      <Field htmlFor={`${idPrefix}-fonte`} label={t.fonteCoordenada}>
        <Select
          id={`${idPrefix}-fonte`}
          data-testid={`${idPrefix}-fonte`}
          value={state.fonteCoordenada}
          disabled={disabled}
          onChange={(e) =>
            patch({ fonteCoordenada: e.target.value as ProtocoloFormState['fonteCoordenada'] })
          }
        >
          {FONTES_COORDENADA.map((fonte) => (
            <option key={fonte} value={fonte}>
              {t.fonteCoordenadaOpcoes[fonte]}
            </option>
          ))}
        </Select>
      </Field>
    </Secao>
  );
}

export function SecaoDispositivo({ idPrefix, state, patch, disabled }: SecaoProps) {
  return (
    <Secao titulo={t.secaoDispositivo}>
      <CampoTexto
        id={`${idPrefix}-user-agent`}
        label={t.userAgent}
        value={state.userAgent}
        onChange={(v) => patch({ userAgent: v })}
        disabled={disabled}
        mono
        maxLength={512}
      />
      <CampoTexto
        id={`${idPrefix}-fingerprint`}
        label={t.fingerprint}
        value={state.fingerprint}
        onChange={(v) => patch({ fingerprint: v })}
        disabled={disabled}
        mono
        maxLength={255}
      />
    </Secao>
  );
}

export function SecaoBiometria({ idPrefix, state, patch, disabled }: SecaoProps) {
  return (
    <Secao titulo={t.secaoBiometria}>
      <label className="flex items-center gap-2 text-[14px] text-fg sm:col-span-2">
        <input
          type="checkbox"
          data-testid={`${idPrefix}-bio-presente`}
          checked={state.biometriaPresente}
          disabled={disabled}
          onChange={(e) => patch({ biometriaPresente: e.target.checked })}
          className="h-4 w-4 accent-accent"
        />
        {t.biometriaPresente}
      </label>
      <CampoTexto
        id={`${idPrefix}-bio-sessao`}
        label={t.biometriaSessaoId}
        value={state.biometriaSessaoId}
        onChange={(v) => patch({ biometriaSessaoId: v })}
        disabled={disabled}
        mono
        maxLength={128}
      />
      <CampoTexto
        id={`${idPrefix}-bio-fornecedor`}
        label={t.biometriaFornecedor}
        value={state.biometriaFornecedor}
        onChange={(v) => patch({ biometriaFornecedor: v })}
        disabled={disabled}
        maxLength={120}
      />
      <CampoTexto
        id={`${idPrefix}-bio-score`}
        label={t.biometriaScore}
        value={state.biometriaScore}
        onChange={(v) => patch({ biometriaScore: v })}
        disabled={disabled}
        mono
        inputMode="decimal"
      />
      <CampoTexto
        id={`${idPrefix}-bio-threshold`}
        label={t.biometriaThreshold}
        value={state.biometriaThreshold}
        onChange={(v) => patch({ biometriaThreshold: v })}
        disabled={disabled}
        mono
        inputMode="decimal"
      />
      <Field htmlFor={`${idPrefix}-bio-prova`} label={t.biometriaProvaDeVida}>
        <Select
          id={`${idPrefix}-bio-prova`}
          data-testid={`${idPrefix}-bio-prova`}
          value={state.biometriaProvaDeVida}
          disabled={disabled}
          onChange={(e) =>
            patch({
              biometriaProvaDeVida: e.target.value as ProtocoloFormState['biometriaProvaDeVida'],
            })
          }
        >
          {(['nao_informado', 'sim', 'nao'] as const).map((opcao) => (
            <option key={opcao} value={opcao}>
              {t.biometriaProvaDeVidaOpcoes[opcao]}
            </option>
          ))}
        </Select>
      </Field>
    </Secao>
  );
}

export function SecaoObservacoes({ idPrefix, state, patch, disabled }: SecaoProps) {
  return (
    <Secao titulo={t.secaoObservacoes}>
      <Field htmlFor={`${idPrefix}-observacoes`} label={t.observacoes} className="sm:col-span-2">
        <Textarea
          id={`${idPrefix}-observacoes`}
          data-testid={`${idPrefix}-observacoes`}
          value={state.observacoes}
          disabled={disabled}
          rows={3}
          maxLength={2000}
          onChange={(e) => patch({ observacoes: e.target.value })}
        />
      </Field>
    </Secao>
  );
}
